import React from 'react';
import { DownOutlined } from '@ant-design/icons';
import isEmpty from 'lodash/isEmpty';
import styled from 'types/theme.types';

const EXPAND_ICON_CLASS_NAME = 'ant-table-row-expand-icon ant-table-row-expand-icon-spaced';

// antd sizes the icon to the glyph, which leaves a target under the 24px minimum. The padding grows the
// clickable area, the margin keeps a misclick away from the row title.
const GLYPH_SIZE = 14;
const TARGET_PADDING = 6;
const TITLE_GAP = 12;

export const EXPAND_ICON_WIDTH = GLYPH_SIZE + TARGET_PADDING * 2 + TITLE_GAP;

/**
 * The row-indent bar's height. `table.tsx` gives the indent span a height because antd ships it at
 * `height: 1px`, and the bar is a float — a float only displaces (indents) content it overlaps
 * vertically, and a 1px float misses the vertically-centred row content entirely. Sized to the row's
 * line-height (20px, `table.tsx`), NOT the taller 26px icon box: at 26px the bar overshot the 20px label
 * and pushed it ~3px above the cell's centre (APO-729 review). 20px still fully overlaps, so it indents.
 */
export const INDENT_BAR_HEIGHT = 20;

/* For nested rows antd passes the record's children instead of a boolean, and an empty array
   of children must not count as expandable. */
export const renderTableExpandIcon = <T,>({
  expanded,
  onExpand,
  record,
  expandable
}: {
  expanded: boolean;
  record: T;
  expandable: boolean | T[];
  onExpand: (record: T, event: React.MouseEvent<HTMLElement>) => void;
}): React.ReactNode =>
  (Array.isArray(expandable) ? !isEmpty(expandable) : expandable) ? (
    <StyledExpandIcon
      className={EXPAND_ICON_CLASS_NAME}
      isExpanded={expanded}
      onClick={e => {
        e.stopPropagation();
        onExpand(record, e);
      }}
      aria-label={expanded ? 'Collapse row' : 'Expand row'}
      aria-expanded={expanded}
    />
  ) : (
    <ExpandIconSlot />
  );

/**
 * Rows with nothing to expand still reserve the icon's width. Without the spacer a leaf sub-row's title
 * lands LEFT of its expandable parent's (indentSize alone is 15px against the icon's 38px), and
 * un-expandable siblings misalign against expandable ones — the projects-tree regression the UI-polish
 * review caught.
 */
const ExpandIconSlot: React.FC = () => <ExpandIconPlaceholder className={EXPAND_ICON_CLASS_NAME} aria-hidden />;

// antd targets the icon with two classes, so a single emotion class loses on specificity.
const targetBox = `
  &&& {
    box-sizing: content-box;
    width: ${GLYPH_SIZE}px;
    height: ${GLYPH_SIZE}px;
    padding: ${TARGET_PADDING}px;
    margin-inline-end: ${TITLE_GAP}px;
    margin-top: 0;
    line-height: 1;
  }
`;

const StyledExpandIcon = styled(DownOutlined)<{ isExpanded: boolean }>`
  ${targetBox};

  &&& {
    visibility: visible;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: transform 200ms ease-in;

    color: var(--component-table-control-icon);

    transform: ${({ isExpanded }) => (isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)')};

    &:hover {
      color: var(--component-table-cell-text-strong);
    }
  }
`;

const ExpandIconPlaceholder = styled.div`
  ${targetBox};
`;
